import { AfterViewInit, Component, ElementRef, ViewChild, OnInit, Input } from '@angular/core';
import * as d3 from 'd3';
import { Subject } from 'rxjs/internal/Subject';
import { IPieChartData } from '../../../models/IPieChartData';
import { PieDataService } from '../../../services/chart/pie-data.service';

@Component({
  selector: 'ff-pie-dou-chart',
  templateUrl: './pie-dou-chart.component.html',
  styleUrls: ['./pie-dou-chart.component.scss']
})
export class PieDouChartComponent implements AfterViewInit,  OnInit {
  @ViewChild("containerPieChart") element: ElementRef;

  /**
   * Data to display use the PieData interface
   * Format: {label: string, value: number, percentage: number}
   * @internal
   */
  @Input() pieData : IPieChartData[] = [];

  /**
   * New data to add to the Pie Chart
   * Format: {label: string, value: number, percentage: number}
   * @internal
   */
  @Input() newData: Subject<IPieChartData[]>;

  /**
   * Subject to toggle betweent pie chart style
   * Default value: false
   * @internal
   */
  @Input() doughnutSubject: Subject<boolean>;

  /**
   * Defines the color scheme for the pie chart
   * Default value: random colors
   * @internal
   */
  @Input() colors =  [];

  /**
   * Subject to toggle inside percentages style
   * Default value: false
   * @internal
   */
   @Input() insideLabelsSubject: Subject<boolean>;

   /**
   * Defines the initial style of the labels
   * Default value: false
   * @internal
   */
    @Input() insideLabels: boolean = false;

   /**
   * Defines the initial style of the pie chart
   * Default value: false
   * @internal
   */
   @Input() isDoughnut: boolean = false;


  //Internal private variables
  private host: d3.Selection<d3.BaseType, {}, d3.BaseType, any>;
  private svg: d3.Selection<SVGElement, {} , d3.BaseType, any>;
  private width: number;
  private height: number;
  private radius: number;
  private htmlElement: HTMLElement;
  private totalData: number = 0;
  private customColors: boolean = false;


  constructor(private dataservice: PieDataService) {} 


  //Subscribes to observables to make modifications to the chart in case of new data
  ngAfterViewInit(): void {
    this.htmlElement = this.element.nativeElement;
    this.host = d3.select(this.htmlElement);
    
    setTimeout(() => {
      this.dataservice.$data.subscribe(data => {    
        this.setup();
        this.buildSVG();
        this.buildPie(); 
      });
  
      this.newData.subscribe(data => {    

        if(!this.customColors)
          this.colors.push(this.random_hex_color_code());
        
          this.dataservice.updateData(data);
      });
      
      this.doughnutSubject.subscribe(data => {    
        this.isDoughnut = data;
        this.setup();
        this.buildSVG();
        this.buildPie(); 
      });

      this.insideLabelsSubject.subscribe(data => {    
        this.insideLabels = data;
        this.setup();
        this.buildSVG();
        this.buildPie(); 
      });
    });
  }

  //Sets data and colors for the chart
  ngOnInit() {
    this.dataservice.setData(this.pieData);
    if(this.colors.length === 0){
      this.colors = this.getColors();
    } else {
      this.customColors = true;
    }

  }

  //Sets total value of the piedata's elements
  private setTotalData() {
    this.totalData = 0;
    this.pieData.forEach(e => {
      this.totalData = this.totalData + e.value;
    });
  }

  //Sets percentages to each piedata's array element
  private setPercentages() {
    this.pieData.forEach(e => {
      const percent = Math.floor((e.value / this.totalData) * 100);
      e.percentage = percent;      
      });
  }

  //Method to populate colors array in case that it is not defined by user
  private getColors(): string[] {
    let colors: string[] = [];

    this.pieData.forEach(e => {
      colors.push(this.random_hex_color_code());
    });
    return colors;
  }

  //Ramdom color generator
  random_hex_color_code = (): string => {
    let color: string = '';
    
    while(color.length < 7) {
    color = '#' + Math.floor((Math.random()) * 16777215).toString(16);
    }
    
    return color;
  };

  //Dimensions for the pie chart
  private setup(): void {
    this.width = 250;
    this.height = 250;
    this.radius = Math.min(this.width, this.height) / 2;
  }

  //Creates svg
  private buildSVG(): void {
    this.host.html("");
    this.svg = this.host.append("svg")
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .append("g")
      .attr("transform", `translate(${this.width / 2}, ${this.height / 2})`);

  }

  //Creates pie element
  private buildPie(): void {
    let pie = d3.pie();
    let values = this.pieData.map(data => data.value);

    let arcSelection = this.svg.selectAll(".arc")
    .data(pie(values))
    .enter()
    .append("g")
    .attr("class", "arc");

    this.populatePie(arcSelection);
  }

  //This method creates the arcs and fill them with color and label
  private populatePie(arcSelection) : void {
    let innerRadius = this.radius - 50;
    let outerRadius = this.radius - 10;

    //Sets total data and percentages 
    this.setTotalData();
    this.setPercentages();

    let pieColor = d3.scaleOrdinal(this.colors);
    //Generate the arcs
    let arc = d3.arc().innerRadius(!this.isDoughnut ? 0 : innerRadius).outerRadius(outerRadius);
    //Draw arc paths
    arcSelection.append("path")
      .attr("d", arc)
      .attr("fill", (data, index) => {
        return pieColor(this.colors[index])
      });

      if (this.insideLabels) {
        //Inserts text inside arcs
        arcSelection.append("text")
        .attr("transform", (datum: any) => {
          datum.innerRadius = 0;
          datum.outerRadius = outerRadius;
          return "translate(" + arc.centroid(datum) + ")";
        })
        .text((datum, index) => (this.pieData[index].percentage > 3 ? this.pieData[index].percentage + '%' : ''))
        .style("font-size",  ".9rem")
        .style("fill", "white")
        .style("text-anchor", "middle")
        .attr("class", "arc-text");
      }    
    } 

}
